import { FinanceLabFieldType } from "./FinanceLab.model";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * Format a Finance Lab metric value based on its field type.
 * Mimics the to_string() methods from core_metrics.py
 */
export const formatFinanceLabValue = (
    value: number | string | boolean | null | undefined,
    fieldType: FinanceLabFieldType,
): string => {
    if (value === null || value === undefined) {
        return "N/A";
    }

    switch (fieldType) {
        case FinanceLabFieldType.CURRENCY:
            return formatCurrency(value as number);

        case FinanceLabFieldType.PERCENTAGE:
            return formatPercentage(value as number);

        case FinanceLabFieldType.RATIO:
            return formatRatio(value as number);

        case FinanceLabFieldType.MULTIPLE:
            return formatMultiple(value as number);

        case FinanceLabFieldType.DATE:
            return formatDate(value as string);

        case FinanceLabFieldType.BOOLEAN:
            return String(value);

        case FinanceLabFieldType.INTEGER:
            return String(value);

        case FinanceLabFieldType.PERIOD:
            return String(value);

        case FinanceLabFieldType.STRING:
        default:
            return String(value);
    }
};

/**
 * Format currency: $1,234.56
 */
const formatCurrency = (value: number): string => {
    if (isNaN(value)) {
        return "N/A";
    }
    return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    }).format(value);
};

/**
 * Format percentage: 50.00%
 * Value is expected to be decimal (0.5 -> 50%)
 */
const formatPercentage = (value: number): string => {
    if (isNaN(value)) {
        return "N/A";
    }
    return `${(value * 100).toFixed(2)}%`;
};

/**
 * Format ratio: 0.50
 */
const formatRatio = (value: number): string => {
    if (isNaN(value)) {
        return "N/A";
    }
    return value.toFixed(2);
};

/**
 * Format multiple: 2.30x
 */
const formatMultiple = (value: number): string => {
    if (isNaN(value)) {
        return "N/A";
    }
    return `${value.toFixed(2)}x`;
};

/**
 * Format date: mm/dd/yyyy -> Dec 10, 2025
 */
const formatDate = (value: string): string => {
    if (!value || value === "-" || value === "NaT") {
        return "-";
    }

    const parsedDate = dayjs(value, "MM/DD/YYYY");

    if (!parsedDate.isValid()) {
        return "-";
    }

    return parsedDate.format("MMM D, YYYY");
};
